import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createProjectArchive,
  getProjectArchive,
  projectArchiveVolumeUrl,
  retryProjectArchive,
  type ProjectArchive,
} from './api';

function sizeLabel(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.ceil(bytes / 1024))} KB`;
}

function statusLabel(status: NonNullable<ProjectArchive['job']>['status']): string {
  if (status === 'VERIFIED') return '生成完了';
  if (status === 'FAILED') return '生成失敗';
  if (status === 'BUILDING') return '生成中';
  return '生成待ち';
}

export function ProjectArchivePanel({
  canGenerate,
  companyId,
  csrfToken,
  onError,
  projectId,
}: {
  canGenerate: boolean;
  companyId: string;
  csrfToken?: string;
  onError: (error: unknown) => void;
  projectId: string;
}) {
  const [archive, setArchive] = useState<ProjectArchive>({ job: null });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const keyRef = useRef(crypto.randomUUID());

  const refresh = useCallback(async () => {
    const value = await getProjectArchive(companyId, projectId);
    setArchive(value);
    return value;
  }, [companyId, projectId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void refresh()
      .catch(onError)
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [onError, refresh]);

  useEffect(() => {
    if (archive.job?.status !== 'QUEUED' && archive.job?.status !== 'BUILDING') return;
    const timer = window.setInterval(() => void refresh().catch(onError), 2500);
    return () => window.clearInterval(timer);
  }, [archive.job?.status, onError, refresh]);

  async function generate() {
    if (csrfToken === undefined) return;
    setBusy(true);
    try {
      const value = await createProjectArchive(companyId, projectId, csrfToken, keyRef.current);
      keyRef.current = crypto.randomUUID();
      setArchive(value);
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function retry() {
    if (csrfToken === undefined || archive.job === null) return;
    setBusy(true);
    try {
      setArchive(await retryProjectArchive(companyId, projectId, archive.job.id, csrfToken));
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="project-archive card">一括保存の状態を確認しています…</div>;
  const job = archive.job;
  const progress =
    job === null || job.totalSourceBytes === 0
      ? 0
      : Math.min(100, Math.round((job.processedBytes / job.totalSourceBytes) * 100));

  return (
    <section className="project-archive card">
      <div className="section-heading">
        <div>
          <p className="eyebrow">PROJECT ARCHIVE</p>
          <h3>工事一括保存</h3>
        </div>
        {canGenerate && job?.status !== 'QUEUED' && job?.status !== 'BUILDING' && (
          <button
            className="secondary"
            disabled={busy}
            onClick={() => void (job?.status === 'FAILED' ? retry() : generate())}
            type="button"
          >
            {job?.status === 'FAILED' ? '失敗した箇所から再開' : '一括保存ZIPを生成'}
          </button>
        )}
      </div>
      <p className="muted">
        公開済み教材を対象年月ごとの「紙・電子納品・社内保管・動画」に整理します。複数ZIPになる場合も各巻を単独で展開できます。
      </p>
      {job === null ? (
        <p className="empty-state">一括保存はまだ生成されていません。</p>
      ) : (
        <>
          <div className="archive-status-line">
            <span className={`status-chip ${job.status.toLowerCase()}`}>
              {statusLabel(job.status)}
            </span>
            <span>
              {job.processedFiles}/{job.totalSourceFiles}ファイル · {job.completedVolumes}/
              {job.plannedVolumes}巻
            </span>
          </div>
          {(job.status === 'QUEUED' || job.status === 'BUILDING') && (
            <div
              aria-label={`一括保存生成 ${progress}%`}
              aria-valuemax={100}
              aria-valuemin={0}
              aria-valuenow={progress}
              className="archive-progress"
              role="progressbar"
            >
              <span style={{ width: `${progress}%` }} />
            </div>
          )}
          {job.errorDetail !== null && <p className="error-message">{job.errorDetail}</p>}
          {job.volumes.length > 0 && (
            <div className="archive-volume-list">
              {job.volumes.map((volume) =>
                volume.status === 'VERIFIED' ? (
                  <a
                    className="archive-volume"
                    href={projectArchiveVolumeUrl(companyId, projectId, job.id, volume.id)}
                    key={volume.id}
                  >
                    <span>
                      <strong>第{volume.volumeNo}巻</strong>
                      <small>{volume.displayName}</small>
                    </span>
                    <span>{sizeLabel(volume.sizeBytes ?? volume.sourceBytes)}</span>
                  </a>
                ) : (
                  <div className="archive-volume pending" key={volume.id}>
                    <span>
                      <strong>第{volume.volumeNo}巻</strong>
                      <small>{volume.sourceFileCount}ファイル</small>
                    </span>
                    <span>{volume.status}</span>
                  </div>
                ),
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
